// The authenticated app shell: a dark left sidebar (icon + label navigation) +
// a light top bar (account name + sign out). Pages render in <Outlet />.
//
// NAV is a plain array — to add a page, add one entry here (with an icon key)
// and one <Route> in App.tsx. That's the whole "register a page" story.

import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

type NavItem = { to: string; label: string; end?: boolean; icon: ReactNode };

// Monoline (lucide-style) icons, drawn inside a shared 24×24 stroke wrapper.
function I({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      {children}
    </svg>
  );
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", end: true, icon: <I><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></I> },
  { to: "/command-center", label: "Command Center", icon: <I><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></I> },
  { to: "/hub-dashboard", label: "Hub Dashboard", icon: <I><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></I> },
  { to: "/hubs", label: "Hubs", icon: <I><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-6h6v6" /></I> },
  { to: "/branches", label: "Branches", icon: <I><line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></I> },
  { to: "/events", label: "Hub Events", icon: <I><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></I> },
  { to: "/event-impact", label: "Event Impact", icon: <I><path d="M13 2 3 14h9l-1 8 10-12h-9z" /></I> },
  { to: "/customers", label: "Customers", icon: <I><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></I> },
  { to: "/import-actuals", label: "Import Actuals", icon: <I><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></I> },
  { to: "/import-branch-actuals", label: "Import Branch Actuals", icon: <I><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="8 12 12 16 16 12" /><line x1="12" y1="3" x2="12" y2="16" /></I> },
  { to: "/forecast", label: "Forecast", icon: <I><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></I> },
  { to: "/branch-forecast", label: "Branch Forecast", icon: <I><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></I> },
  { to: "/forecast-lock", label: "Forecast Lock", icon: <I><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></I> },
  { to: "/forecast-planner", label: "Forecast Planner", icon: <I><rect x="3" y="4" width="18" height="17" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="9" x2="9" y2="21" /><line x1="15" y1="9" x2="15" y2="21" /></I> },
  { to: "/analytics", label: "Analytics", icon: <I><path d="M3 3v18h18" /><circle cx="9" cy="11" r="1.6" /><circle cx="13" cy="15" r="1.6" /><circle cx="18" cy="7" r="1.6" /><path d="m10.3 10 1.4 3.4M14.4 14l2.4-5.6" /></I> },
  { to: "/routing-map", label: "Routing Map", icon: <I><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21 1 6" /><line x1="8" y1="3" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="21" /></I> },
  { to: "/volume-availability", label: "Volume Availability", icon: <I><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="14" width="3" height="4" /></I> },
  { to: "/workbench", label: "Workbench", icon: <I><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></I> },
  { to: "/calibration", label: "Calibration", icon: <I><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></I> },
  { to: "/seasonal", label: "Seasonal", icon: <I><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></I> },
  { to: "/year-overview", label: "Year Overview", icon: <I><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="16" y1="2" x2="16" y2="6" /><path d="M8 15h.01M12 15h.01M16 15h.01" /></I> },
  { to: "/yearly", label: "Yearly", icon: <I><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /></I> },
  { to: "/guidelines", label: "Guidelines & SOP", icon: <I><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></I> },
];

export default function Layout() {
  const { activeAccount, signOut } = useAuth();

  return (
    <div className="flex h-full">
      <aside className="flex w-60 flex-col bg-[#0b1220] text-zinc-400">
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
            O
          </span>
          <div className="text-base font-semibold tracking-tight text-white">OpsPanther</div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={isActive ? "text-brand-400" : "text-zinc-500 group-hover:text-zinc-300"}>
                    {item.icon}
                  </span>
                  <span className="truncate">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
          <div className="text-sm text-slate-500">{activeAccount?.name ?? "—"}</div>
          <button onClick={signOut} className="text-sm font-medium text-slate-600 hover:text-slate-900">
            Sign out
          </button>
        </header>
        <main className="flex-1 overflow-auto bg-[#fafafa] p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
